import { useEffect, useMemo, useRef, useState } from "react";
import {
  BURRARD_POLYMARKET_HOME_URL,
  type WorkspaceRole,
} from "@burrard/shared";

type MarketingRoute = "landing" | "paid" | "demo" | "admin";
type CheckoutTone = "success" | "warning";

type CheckoutBanner = {
  tone: CheckoutTone;
  title: string;
  body: string;
};

type ScreenshotCard = {
  src: string;
  alt: string;
  label: string;
  title: string;
  body: string;
};

const founderPrice = "$300";

const heroStats = [
  { label: "Pipeline", value: "Live NOAA + Polymarket" },
  { label: "Board", value: "Live market workspace" },
  { label: "Access", value: "Paid controls unlock" },
] as const;

const screenshotCards: ScreenshotCard[] = [
  {
    src: "/screenshots/terminal-board.png",
    alt: "Burrard Desk paid terminal overview with map, signals, and execution panels.",
    label: "Paid terminal",
    title: "Map-first command surface",
    body: "The paid workspace keeps the live weather board, market mismatch grid, and execution controls in one surface.",
  },
  {
    src: "/screenshots/terminal-map.png",
    alt: "Burrard Desk live weather map showing city nodes and market mismatches.",
    label: "Live board",
    title: "Weather context on the map",
    body: "Station nodes, forecast context, and edge concentration stay visible without hiding the market state.",
  },
  {
    src: "/screenshots/terminal-grid.png",
    alt: "Burrard Desk paid terminal showing the live signal feed and weather-versus-market table.",
    label: "Data streams",
    title: "Live grid and signal tape",
    body: "The paid board keeps the live mismatch tape and dense weather-versus-market table active beside the map.",
  },
];

const productNotes = [
  "Live data, signals, and weather boards stay synchronized across access tiers.",
  "Paid access unlocks order building, submission, and execution mode controls.",
  "Paid users can connect a Polymarket wallet directly from this access page.",
  "Administrator sign-in can open the full terminal directly.",
] as const;

function isLocalPreviewHost(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  return window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost";
}

function readMarketingLocation(): { route: MarketingRoute; params: URLSearchParams } {
  if (typeof window === "undefined") {
    return { route: "landing", params: new URLSearchParams() };
  }
  const rawHash = window.location.hash.replace(/^#/, "").trim();
  if (!rawHash) {
    return { route: "landing", params: new URLSearchParams(window.location.search) };
  }
  const [pathPart, queryPart = ""] = rawHash.split("?");
  const normalizedPath = pathPart.replace(/^\/+/, "").toLowerCase();
  return {
    route:
      normalizedPath === "paid"
        ? "paid"
        : normalizedPath === "demo"
        ? "demo"
        : normalizedPath === "admin"
        ? "admin"
        : "landing",
    params: new URLSearchParams(queryPart),
  };
}

function buildMarketingRouteUrl(route: MarketingRoute, params?: URLSearchParams): string {
  const query = params?.toString();
  if (route === "landing") {
    return `#/${query ? `?${query}` : ""}`;
  }
  return `#/${route}${query ? `?${query}` : ""}`;
}

function writeMarketingLocation(route: MarketingRoute, params?: URLSearchParams): void {
  if (typeof window === "undefined") {
    return;
  }
  const query = params?.toString();
  let nextHash = "";
  if (route === "landing") {
    nextHash = `/${query ? `?${query}` : ""}`;
  } else if (route === "paid") {
    nextHash = `/paid${query ? `?${query}` : ""}`;
  } else if (route === "demo") {
    nextHash = `/demo${query ? `?${query}` : ""}`;
  } else if (route === "admin") {
    nextHash = `/admin${query ? `?${query}` : ""}`;
  }
  if (window.location.hash.replace(/^#/, "") !== nextHash) {
    window.location.hash = nextHash;
  }
}

function derivePortOrigin(targetPort: number): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const current = new URL(window.location.href);
  if (current.hostname === "localhost" || current.hostname === "127.0.0.1") {
    return `${current.protocol}//${current.hostname}:${targetPort}`;
  }

  if (current.hostname.endsWith(".app.github.dev")) {
    const host = current.hostname.replace(/-\d+(\.app\.github\.dev)$/i, `-${targetPort}$1`);
    return `${current.protocol}//${host}`;
  }

  if (current.port) {
    current.port = String(targetPort);
    return current.origin;
  }

  return null;
}

function workspaceBaseUrl(route: WorkspaceRole): string {
  if (route === "demo") {
    return (
      import.meta.env.VITE_PUBLIC_DEMO_URL ??
      derivePortOrigin(5173) ??
      "https://demo.burrarddesk.com"
    );
  }

  return (
    import.meta.env.VITE_PUBLIC_APP_URL ??
    derivePortOrigin(5173) ??
    "https://app.burrarddesk.com"
  );
}

function buildWorkspaceUrl(route: WorkspaceRole, sessionId?: string | null, authToken?: string | null): string {
  const sanitized = workspaceBaseUrl(route).replace(/#.*$/, "").replace(/\/$/, "");
  if (route === "demo") {
    return `${sanitized}/#/demo`;
  }
  if (route === "admin") {
    const adminQuery = authToken ? `?auth=${encodeURIComponent(authToken)}` : "";
    return `${sanitized}/#/admin${adminQuery}`;
  }
  const params = new URLSearchParams();
  if (sessionId) {
    params.set("session_id", sessionId);
  }
  if (authToken) {
    params.set("auth", authToken);
  }
  const query = params.toString();
  return `${sanitized}/#/paid${query ? `?${query}` : ""}`;
}

function buildApiUrl(path: string): string {
  const base =
    import.meta.env.VITE_API_BASE_URL ??
    derivePortOrigin(8000) ??
    "https://burrarddesk-api.onrender.com";
  return `${base.replace(/\/$/, "")}${path}`;
}

function readCheckoutBanner(params: URLSearchParams): CheckoutBanner | null {
  const checkout = params.get("checkout");
  if (checkout === "success") {
    return {
      tone: "success",
      title: "Payment captured.",
      body: "Your paid workspace is ready to open from this page.",
    };
  }
  if (checkout === "cancelled") {
    return {
      tone: "warning",
      title: "Checkout cancelled.",
      body: "No charge was made. The live board remains open while you review the workspace.",
    };
  }
  return null;
}

export function App() {
  const [route, setRoute] = useState<MarketingRoute>(() => readMarketingLocation().route);
  const [checkoutState, setCheckoutState] = useState<"idle" | "loading" | "error">("idle");
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [adminError, setAdminError] = useState<string | null>(null);
  const [adminFieldsUnlocked, setAdminFieldsUnlocked] = useState(false);
  const adminEmailRef = useRef<HTMLInputElement | null>(null);
  const adminPasswordRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const handleHashChange = () => {
      setRoute(readMarketingLocation().route);
    };
    window.addEventListener("hashchange", handleHashChange);
    if (!window.location.hash) {
      writeMarketingLocation(route);
    }
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [route]);

  const locationState = readMarketingLocation();
  const sessionId = locationState.params.get("session_id");
  const section = locationState.params.get("section");
  const checkoutBanner = useMemo(() => readCheckoutBanner(locationState.params), [locationState.params.toString()]);
  const demoWorkspaceUrl = useMemo(() => buildWorkspaceUrl("demo"), []);
  const paidWorkspaceUrl = useMemo(() => buildWorkspaceUrl("paid", sessionId), [sessionId]);
  const paidWorkspacePreviewUrl = useMemo(() => buildWorkspaceUrl("paid"), []);
  const productSectionUrl = useMemo(() => buildMarketingRouteUrl("landing", new URLSearchParams({ section: "product" })), []);
  const screensSectionUrl = useMemo(() => buildMarketingRouteUrl("landing", new URLSearchParams({ section: "screens" })), []);
  const localPreview = isLocalPreviewHost();
  const unlockAdminFields = () => setAdminFieldsUnlocked(true);

  useEffect(() => {
    if (route !== "landing" || !section) {
      return;
    }

    const element = document.getElementById(section);
    if (!element) {
      return;
    }

    window.requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [route, section]);
  useEffect(() => {
    if (localPreview || route !== "demo") {
      return;
    }
    window.location.replace(demoWorkspaceUrl);
  }, [demoWorkspaceUrl, localPreview, route]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    // Clear any browser/password-manager autofill that may race in after hydration.
    const clearFields = () => {
      setAdminEmail("");
      setAdminPassword("");
      if (adminEmailRef.current) {
        adminEmailRef.current.value = "";
      }
      if (adminPasswordRef.current) {
        adminPasswordRef.current.value = "";
      }
    };

    clearFields();
    const frame = window.requestAnimationFrame(clearFields);
    const timeout = window.setTimeout(clearFields, 150);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timeout);
    };
  }, [route]);

  useEffect(() => {
    if (typeof window === "undefined" || adminFieldsUnlocked) {
      return;
    }

    const interval = window.setInterval(() => {
      if (adminEmailRef.current?.value) {
        adminEmailRef.current.value = "";
      }
      if (adminPasswordRef.current?.value) {
        adminPasswordRef.current.value = "";
      }
      setAdminEmail("");
      setAdminPassword("");
    }, 250);

    const timeout = window.setTimeout(() => window.clearInterval(interval), 4000);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [adminFieldsUnlocked, route]);

  const handleCheckout = async () => {
    setCheckoutState("loading");
    setCheckoutError(null);
    try {
      const response = await fetch("/api/create-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const payload = (await response.json()) as { url?: string; error?: string };
      if (!response.ok || !payload.url) {
        throw new Error(payload.error ?? "Stripe checkout could not be started.");
      }
      window.location.assign(payload.url);
      return;
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : "Stripe checkout could not be started.");
      setCheckoutState("error");
    } finally {
      setCheckoutState("idle");
    }
  };

  const handleAdminLogin = async (destination: WorkspaceRole = "paid") => {
    setAdminError(null);

    try {
      const response = await fetch(buildApiUrl("/auth/admin/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: adminEmail, password: adminPassword }),
      });
      const payload = (await response.json()) as { token?: string; detail?: string };
      if (!response.ok || !payload.token) {
        throw new Error(payload.detail ?? "Admin sign-in could not be completed.");
      }
      setAdminPassword("");
      window.location.assign(buildWorkspaceUrl(destination, null, payload.token));
    } catch (error) {
      setAdminError(error instanceof Error ? error.message : "Admin sign-in could not be completed.");
    }
  };

  if (route === "paid") {
    return (
      <div className="marketing-shell">
        <header className="marketing-topbar">
          <a className="marketing-brand" href="#/">
            <span className="brand-copy">
              <strong>Burrard Desk</strong>
            </span>
          </a>

          <div className="marketing-actions">
            <a className="marketing-link" href="#/">
              Landing
            </a>
            <a className="marketing-link" href={demoWorkspaceUrl}>
              Live board
            </a>
            <a className="marketing-link" href="#/admin">
              Admin
            </a>
          </div>
        </header>

        {checkoutBanner ? (
          <div className={`checkout-banner checkout-banner-${checkoutBanner.tone}`}>
            <strong>{checkoutBanner.title}</strong>
            <span>{checkoutBanner.body}</span>
          </div>
        ) : null}

        <main className="checkout-layout">
          <section className="checkout-panel checkout-panel-primary">
            <div className="eyebrow">Paid access</div>
            <h1>Unlock the trading workspace.</h1>
            <p className="lead-copy">
              The live board stays open for review. Paid access unlocks order building, submission, automation, and live trading controls.
            </p>

            <div className="price-card">
              <strong>{founderPrice}</strong>
              <span>Access for the current workspace build.</span>
            </div>

            <div className="button-row">
              <button className="button button-primary" onClick={() => void handleCheckout()} disabled={checkoutState === "loading"}>
                {checkoutState === "loading" ? "Opening Stripe" : "Start secure checkout"}
              </button>
              <a className="button" href={demoWorkspaceUrl}>
                Open live board
              </a>
              {sessionId ? (
                <a className="button" href={paidWorkspaceUrl}>
                  Open trading workspace
                </a>
              ) : null}
              {localPreview && !sessionId ? (
                <a className="button" href={paidWorkspacePreviewUrl}>
                  Open workspace
                </a>
              ) : null}
            </div>

            {checkoutError ? <p className="error-copy">{checkoutError}</p> : null}

            <ul className="simple-list">
              {productNotes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>

            <article className="detail-card">
              <div className="eyebrow">Polymarket</div>
              <h2>Connect your wallet</h2>
              <p className="panel-copy">
                Connect your Polymarket wallet, then return here to open the paid workspace.
              </p>
              <a className="button button-primary" href={BURRARD_POLYMARKET_HOME_URL} target="_blank" rel="noreferrer">
                Connect Polymarket wallet
              </a>
            </article>
          </section>

          <aside className="checkout-rail">
            <article className="checkout-panel screenshot-panel">
              <div className="eyebrow">Paid terminal</div>
              <h2>What buyers unlock</h2>
              <img src={screenshotCards[0].src} alt={screenshotCards[0].alt} className="terminal-shot" />
              <p className="panel-copy">{screenshotCards[0].body}</p>
            </article>

            <article className="checkout-panel admin-panel">
              <div className="eyebrow">Administrator access</div>
              <h2>Sign in to the full workspace</h2>
              <div className="auth-form">
                <div className="auth-decoy-fields" aria-hidden="true">
                  <input type="text" name="username" autoComplete="username" tabIndex={-1} defaultValue="" />
                  <input type="password" name="password" autoComplete="current-password" tabIndex={-1} defaultValue="" />
                </div>
                <label>
                  <span>Email</span>
                  <input
                    ref={adminEmailRef}
                    type="email"
                    name="workspace-contact"
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck={false}
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                    readOnly={!adminFieldsUnlocked}
                    onFocus={unlockAdminFields}
                    onMouseDown={unlockAdminFields}
                    onKeyDown={unlockAdminFields}
                    value={adminEmail}
                    onChange={(event) => setAdminEmail(event.target.value)}
                  />
                </label>
                <label>
                  <span>Password</span>
                  <input
                    ref={adminPasswordRef}
                    type="password"
                    name="workspace-secret"
                    autoComplete="new-password"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                    readOnly={!adminFieldsUnlocked}
                    onFocus={unlockAdminFields}
                    onMouseDown={unlockAdminFields}
                    onKeyDown={unlockAdminFields}
                    value={adminPassword}
                    onChange={(event) => setAdminPassword(event.target.value)}
                  />
                </label>
                <div className="button-row">
                  <button className="button button-primary" onClick={() => void handleAdminLogin("paid")}>
                    Enter paid workspace
                  </button>
                  <button className="button" onClick={() => void handleAdminLogin("admin")}>
                    Open admin workspace
                  </button>
                </div>
                {adminError ? <p className="error-copy">{adminError}</p> : null}
              </div>
            </article>
            <article className="checkout-panel disclaimer-panel">
              <div className="eyebrow">Disclosure</div>
              <p className="panel-copy">
                Burrard Desk is an operator workspace for market research and execution support. Availability and trading access depend on
                exchange rules, jurisdiction, and live runtime configuration.
              </p>
            </article>
          </aside>
        </main>
      </div>
    );
  }

  if (route === "demo") {
    if (!localPreview) {
      return null;
    }
    return (
      <div className="marketing-shell">
        <header className="marketing-topbar">
          <a className="marketing-brand" href="#/">
            <span className="brand-copy">
              <strong>Burrard Desk</strong>
            </span>
          </a>
          <div className="marketing-actions">
            <a className="marketing-link" href="#/">
              Landing
            </a>
            <a className="marketing-link" href="#/paid">
              Buy access
            </a>
            <a className="marketing-link" href="#/admin">
              Admin
            </a>
          </div>
        </header>

        <main className="checkout-layout">
          <section className="checkout-panel checkout-panel-primary">
            <div className="eyebrow">Demo workspace</div>
            <h1>Live board preview</h1>
            <p className="lead-copy">
              This page is the demo entry point for the live board. It now has its own route so demo access is separated from landing and paid.
            </p>

            <div className="button-row">
              <a className="button button-primary" href="#/demo">
                Refresh demo page
              </a>
              <a className="button" href="#/paid">
                Buy access
              </a>
            </div>

            <p className="panel-copy">
              Use the demo board to review the live surface before moving into paid access.
            </p>

            <article className="detail-card disclaimer-panel">
              <div className="eyebrow">Disclosure</div>
              <p className="panel-copy">
                The demo surface is for product review. It does not represent a guarantee of live trading availability in every region or runtime.
              </p>
            </article>
          </section>
        </main>
      </div>
    );
  }

  if (route === "admin") {
    return (
      <div className="marketing-shell">
        <header className="marketing-topbar">
          <a className="marketing-brand" href="#/">
            <span className="brand-copy">
              <strong>Burrard Desk</strong>
            </span>
          </a>
          <div className="marketing-actions">
            <a className="marketing-link" href="#/">
              Landing
            </a>
            <a className="marketing-link" href={demoWorkspaceUrl}>
              Live board
            </a>
            <a className="marketing-link" href="#/paid">
              Buy access
            </a>
          </div>
        </header>

        <main className="checkout-layout">
          <section className="checkout-panel checkout-panel-primary">
            <div className="eyebrow">Administrator access</div>
            <h1>Admin workspace entry</h1>
            <p className="lead-copy">
              Use this page to sign in as an administrator and access the full workspace controls.
            </p>

            <div className="auth-form">
              <div className="auth-decoy-fields" aria-hidden="true">
                <input type="text" name="username" autoComplete="username" tabIndex={-1} defaultValue="" />
                <input type="password" name="password" autoComplete="current-password" tabIndex={-1} defaultValue="" />
              </div>
              <label>
                <span>Email</span>
                <input
                  ref={adminEmailRef}
                  type="email"
                  name="workspace-contact"
                  autoComplete="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  readOnly={!adminFieldsUnlocked}
                  onFocus={unlockAdminFields}
                  onMouseDown={unlockAdminFields}
                  onKeyDown={unlockAdminFields}
                  value={adminEmail}
                  onChange={(event) => setAdminEmail(event.target.value)}
                />
              </label>
              <label>
                <span>Password</span>
                <input
                  ref={adminPasswordRef}
                  type="password"
                  name="workspace-secret"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  readOnly={!adminFieldsUnlocked}
                  onFocus={unlockAdminFields}
                  onMouseDown={unlockAdminFields}
                  onKeyDown={unlockAdminFields}
                  value={adminPassword}
                  onChange={(event) => setAdminPassword(event.target.value)}
                />
              </label>

              <div className="button-row">
                <button className="button button-primary" onClick={() => void handleAdminLogin("admin")}>
                  Open admin workspace
                </button>
                <button className="button" onClick={() => void handleAdminLogin("paid")}>
                  Open paid workspace
                </button>
                <a className="button" href="#/paid">
                  Back to paid access
                </a>
              </div>

              {adminError ? <p className="error-copy">{adminError}</p> : null}
            </div>

            <article className="detail-card disclaimer-panel">
              <div className="eyebrow">Disclosure</div>
              <p className="panel-copy">
                Administrator access is for internal operations. Live execution remains subject to exchange, jurisdiction, and runtime controls.
              </p>
            </article>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="marketing-shell">
      <header className="marketing-topbar">
        <a className="marketing-brand" href="#/">
          <span className="brand-copy">
            <strong>Burrard Desk</strong>
          </span>
        </a>

        <div className="marketing-actions">
          <a className="marketing-link" href={productSectionUrl}>
            Product
          </a>
          <a className="marketing-link" href={screensSectionUrl}>
            Screens
          </a>
          <a className="marketing-link" href="#/paid">
            Buy access
          </a>
        </div>
      </header>

      <main className="landing-stack">
        <section className="hero-shell">
          <div className="hero-copy">
            <div className="eyebrow">Live weather markets</div>
            <h1>Trade weather market mispricings from a map-first terminal.</h1>
            <p className="lead-copy">
              Burrard Desk turns live aviation weather, forecast context, and Polymarket contract pricing into a single trading workspace.
            </p>

            <div className="button-row">
              <a className="button button-primary" href={demoWorkspaceUrl}>
                Open live board
              </a>
              <a className="button" href="#/paid">
                Buy access
              </a>
            </div>

            <div className="hero-stat-grid">
              {heroStats.map((item) => (
                <div key={item.label} className="hero-stat">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </div>

          <div className="hero-shot-frame">
            <img src={screenshotCards[0].src} alt={screenshotCards[0].alt} className="terminal-shot terminal-shot-hero" />
          </div>
        </section>

        <section className="story-grid" id="product">
          <article className="story-card story-card-wide">
            <div className="eyebrow">How it works</div>
            <h2>One live pipeline. Two access levels.</h2>
            <p className="panel-copy">
              Users can review the live market board first, then unlock order controls inside the paid terminal without switching to a different product surface.
            </p>
            <ul className="simple-list">
              {productNotes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </article>

          <article className="story-card">
            <div className="eyebrow">Access</div>
            <h2>Simple flow</h2>
            <p className="panel-copy">Users land here, inspect the live board, and unlock the trading workspace from the access page.</p>
          </article>
        </section>

        <section className="screens-grid" id="screens">
          {screenshotCards.map((card) => (
            <article key={card.title} className="screen-card">
              <img src={card.src} alt={card.alt} className="terminal-shot" />
              <div className="screen-copy">
                <span className="eyebrow">{card.label}</span>
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </div>
            </article>
          ))}
        </section>

        <section className="cta-band">
          <div>
            <div className="eyebrow">Ready to review it live</div>
            <h2>Open the live board or go straight to paid access.</h2>
          </div>
          <div className="button-row">
            <a className="button button-primary" href={demoWorkspaceUrl}>
              Open live board
            </a>
            <a className="button" href="#/paid">
              Buy access
            </a>
          </div>
        </section>

        <section className="detail-card disclaimer-panel">
          <div className="eyebrow">Disclosure</div>
          <p className="panel-copy">
            Burrard Desk provides market research and execution tooling. Trading availability depends on exchange policies, jurisdiction, and configured live infrastructure.
          </p>
        </section>
      </main>
    </div>
  );
}
